import { isWordmark, techStack, type TechEntry } from "@/data/tech";
import { useLocale } from "@/i18n/locale";
import { ui } from "@/i18n/ui";

function TechItem({ entry }: { entry: TechEntry }) {
  return (
    // text-muted, not text-faint: faint is for tertiary detail sitting next to
    // brighter text, and here there is nothing brighter to read it against.
    <li className="flex shrink-0 items-center gap-2.5 text-muted transition-colors duration-200 hover:text-accent">
      {isWordmark(entry) ? (
        <span className="font-mono text-base font-medium tracking-tight">{entry.label}</span>
      ) : (
        <>
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="size-5"
            fill="currentColor"
            focusable="false"
          >
            <path d={entry.path} />
          </svg>
          <span className="font-mono text-sm whitespace-nowrap">{entry.label}</span>
        </>
      )}
    </li>
  );
}

function Track({ ariaHidden }: { ariaHidden: boolean }) {
  return (
    <ul
      // The second copy exists only so the loop has something to scroll into.
      // Without hiding it, every technology is announced twice.
      aria-hidden={ariaHidden || undefined}
      className="flex shrink-0 items-center gap-10 pr-10"
    >
      {techStack.map((entry) => (
        <TechItem key={entry.label} entry={entry} />
      ))}
    </ul>
  );
}

export function TechMarquee() {
  const { t } = useLocale();

  return (
    <section
      aria-label={t(ui.techStack)}
      // overflow-hidden is load-bearing, not cosmetic: the track is two full
      // copies wide (~5000px), and the mask below only affects painting, not
      // layout. Without this the whole page scrolls sideways at every viewport.
      className="marquee-mask overflow-hidden border-t border-border py-8"
      // Fades the edges instead of hard-cutting mid-logo. Duplicated as
      // -webkit- for Safari, which still needs the prefix for mask-image.
      style={{
        WebkitMaskImage:
          "linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)",
        maskImage: "linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)",
      }}
    >
      <div className="marquee flex w-max">
        <Track ariaHidden={false} />
        <Track ariaHidden />
      </div>
    </section>
  );
}
